import { MoreOutlined } from '@ant-design/icons'
import type { ColumnDef } from '@tanstack/react-table'
import type { MenuProps } from 'antd'
import { Button, Dropdown, Tooltip } from 'antd'

import { SegmentModel, SegmentStatusType } from '@/types'

import cn from './SegmentsTab.module.scss'

export type SegmentOwner = 'my' | 'partners'

export const SEGMENT_OWNER_OPTIONS: { value: SegmentOwner; label: string }[] = [
  { value: 'my', label: 'Мои сегменты' },
  { value: 'partners', label: 'Сегменты партнёров' },
]

export const SEGMENTS_COLUMN_WIDTHS: Partial<Record<string, string>> = {
  status: '230px',
  description: 'minmax(200px, 1fr)',
  type: '130px',
  mode: 'minmax(180px, 1fr)',
  priceModifierPercentage: '160px',
  markupPercentage: '200px',
  actions: '64px',
}

const formatPercentage = (value: number) => String(value).replace('.', ',')

const STATUS_CONFIG: Record<
  SegmentStatusType,
  { label: string; cls: string; tooltip: string }
> = {
  NEW: {
    label: 'Новый',
    cls: cn.statusNew,
    tooltip: 'Черновик. Сегмент не виден клиентам и партнёрам.',
  },
  AVAILABLE: {
    label: 'Доступный',
    cls: cn.statusAvailable,
    tooltip: 'Активен. Используется для расчёта цен.',
  },
  ARCHIVED: {
    label: 'В архиве',
    cls: cn.statusArchived,
    tooltip:
      'Перемещён в архив вручную. Цены по нему больше не рассчитываются.',
  },
  SUSPENDED_BY_BASE: {
    label: 'Приостановлен (нет базы)',
    cls: cn.statusSuspendedByBase,
    tooltip: 'Базовый сегмент был переведён в архив. Цены недоступны.',
  },
  SUSPENDED_BY_PARTNERSHIP: {
    label: 'Приостановлен (партнёрство)',
    cls: cn.statusSuspendedByPartnership,
    tooltip:
      'Обмен с партнёром расторгнут/приостановлен. Базовый сегмент недоступен.',
  },
  SUSPENDED_BY_ACCESS_REVOKED: {
    label: 'Приостановлен (доступ закрыт)',
    cls: cn.statusSuspendedByAccessRevoked,
    tooltip:
      'Владелец базового сегмента закрыл к нему доступ в настройках партнёрства с Вами.',
  },
}

export const getSegmentStatusLabel = (status: SegmentStatusType) =>
  STATUS_CONFIG[status]?.label ?? status

export const getTanstackColumns = (params: {
  innerTab: SegmentOwner
  onEdit: (segment: SegmentModel) => void
  onChangeStatus: (id: string, status: SegmentStatusType) => void
  onCreateBasedOn: (segment: SegmentModel) => void
}): ColumnDef<SegmentModel, unknown>[] => {
  const { innerTab, onEdit, onChangeStatus, onCreateBasedOn } = params

  return [
    {
      id: 'status',
      header: 'Статус',
      cell: (info) => {
        const status = info.row.original.status
        const config = STATUS_CONFIG[status] || {
          label: status,
          cls: cn.statusNew,
          tooltip: '',
        }
        return (
          <Tooltip title={config.tooltip} placement="top">
            <span className={config.cls}>{config.label}</span>
          </Tooltip>
        )
      },
    },

    {
      id: 'description',
      header: 'Наименование',
      cell: (info) => (
        <span className={cn.cellText}>
          {info.row.original.description}
          {info.row.original.isRrc && (
            <Tooltip title="Из этого сегмента берётся РРЦ во всём ЛК">
              <span className={cn.rrcBadge}>РРЦ</span>
            </Tooltip>
          )}
        </span>
      ),
    },

    {
      id: 'type',
      header: 'Тип',
      cell: () => (innerTab === 'my' ? 'Собственный' : 'Внешний'),
    },

    {
      id: 'mode',
      header: 'Режим / Партнёр',
      cell: (info) => {
        const record = info.row.original
        if (innerTab === 'partners') {
          return record.supplierShortModel?.description || '—'
        }
        return record.baseSegmentShortModel
          ? `От базового «${record.baseSegmentShortModel.description}»`
          : 'Свои цены'
      },
    },

    {
      id: 'priceModifierPercentage',
      header: 'Наценка / Скидка',
      cell: (info) => {
        const val = info.row.original.priceModifierPercentage
        if (innerTab === 'partners' || val === 0)
          return <span className={cn.emptyCell}> — </span>
        const prefix = val > 0 ? '+' : ''
        return (
          <span
            className={`${cn.deltaValue} ${val > 0 ? cn.deltaPositive : cn.deltaNegative}`}
          >
            {prefix}
            {val}%
          </span>
        )
      },
    },

    {
      id: 'markupPercentage',
      header: 'Наценка по умолчанию, %',
      cell: (info) =>
        innerTab === 'partners' ? (
          <span className={cn.emptyCell}> — </span>
        ) : (
          formatPercentage(info.row.original.markupPercentage)
        ),
    },

    {
      id: 'actions',
      header: '',
      cell: (info) => {
        const record = info.row.original
        const items: MenuProps['items'] = []

        if (innerTab === 'my') {
          if (record.status === 'NEW') {
            items.push({
              key: 'publish',
              label: 'Опубликовать',
              onClick: () => onChangeStatus(record.id, 'AVAILABLE'),
            })
            items.push({
              key: 'edit',
              label: 'Редактировать',
              onClick: () => onEdit(record),
            })
          } else if (record.status === 'AVAILABLE') {
            items.push({
              key: 'edit',
              label: 'Редактировать',
              onClick: () => onEdit(record),
            })
            items.push({
              key: 'archive',
              label: 'В архив',
              onClick: () => onChangeStatus(record.id, 'ARCHIVED'),
            })
          } else if (record.status === 'ARCHIVED') {
            items.push({
              key: 'edit',
              label: 'Редактировать',
              onClick: () => onEdit(record),
            })
            items.push({
              key: 'restore',
              label: 'Восстановить',
              onClick: () => onChangeStatus(record.id, 'AVAILABLE'),
            })
          } else if (
            record.status === 'SUSPENDED_BY_BASE' ||
            record.status === 'SUSPENDED_BY_PARTNERSHIP' ||
            record.status === 'SUSPENDED_BY_ACCESS_REVOKED'
          ) {
            items.push({
              key: 'edit',
              label: 'Редактировать',
              onClick: () => onEdit(record),
            })
            items.push({
              key: 'archive',
              label: 'В архив',
              onClick: () => onChangeStatus(record.id, 'ARCHIVED'),
            })
          }
        } else {
          items.push({
            key: 'base',
            label: 'Создать сегмент на базе этого',
            onClick: () => onCreateBasedOn(record),
          })
        }

        return items.length > 0 ? (
          <Dropdown
            menu={{ items }}
            trigger={['hover']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        ) : null
      },
    },
  ]
}
